import { useCallback } from 'react';
import { useShowToast } from '@/hooks';
import { activitySaveFailureMessage } from '@/utils/errors/activitySaveFailure';
import {
  validateEmailFields,
  validateVariables,
  getFromPayload,
  validateToAddress,
} from '../utils/survey-form.utils';

/**
 * One shape for every outcome, so a caller can read `failureMessage` without
 * narrowing the union first — `SurveyForm` toasts it on the schedule path.
 */
export interface SubmitSurveyResult {
  success: boolean;
  data?: any;
  errors?: any;
  /** The reason to show, resolved from whichever envelope carried it. */
  failureMessage?: string;
}

interface UseSurveyFormSubmitParams {
  isCreateFlow: boolean;
  successMessage: {
    create: string;
    update: string;
  };
  errorMessage: {
    create: string;
    update: string;
  };
  onSave: (params: any) => Promise<{ data?: any; errors?: any[] }>;
  selectedTemplate: any;
  feSharedEmails: boolean;
  processFromUserPayload: (params: any) => any;
  isInterviewPlan?: boolean;
}

export const useSurveyFormSubmit = ({
  isCreateFlow,
  successMessage,
  errorMessage,
  onSave,
  selectedTemplate,
  feSharedEmails,
  processFromUserPayload,
  isInterviewPlan = false,
}: UseSurveyFormSubmitParams) => {
  const toast = useShowToast();

  const handleSaveSuccess = useCallback(
    (isSchedule: boolean = false) => {
      if (isSchedule) {
        return;
      }
      toast({
        title: isCreateFlow ? successMessage.create : successMessage.update,
      });
    },
    [isCreateFlow, successMessage, toast]
  );

  const handleSaveError = useCallback(
    (result: any, isSchedule: boolean = false) => {
      if (isSchedule) {
        return;
      }
      toast({
        title: isCreateFlow ? errorMessage.create : errorMessage.update,
        status: 'error',
        // `result.errors` only exists on the send-survey drawer's hand-built
        // shape; the interview plan saves through RTK Query, which reports a
        // refusal as `result.error`. Reading one key meant the other flow's
        // reason — "Sender's mailbox is not connected" among them — was always
        // replaced by the generic line (ENG-4001).
        description: activitySaveFailureMessage(result),
      });
    },
    [isCreateFlow, errorMessage, toast]
  );

  const validateAndBuildPayload = useCallback(
    (detailsValues: any, emailValues: any) => {
      const {
        subject,
        body,
        from,
        fromUserObject,
        variablesListArr,
        toAddress,
      } = emailValues;

      // Validate toAddress for HM surveys
      if (
        !validateToAddress(
          detailsValues.survey_type,
          toAddress,
          toast,
          isInterviewPlan
        )
      ) {
        return null;
      }

      // Validate email fields
      if (
        !validateEmailFields(
          from,
          subject,
          body,
          toast,
          detailsValues.survey_type,
          isInterviewPlan
        )
      ) {
        return null;
      }

      // Get from payload
      const fromPayload = getFromPayload(
        from,
        fromUserObject,
        feSharedEmails,
        processFromUserPayload
      );

      if (!fromPayload.ats_shared_email_id) {
        fromPayload.ats_shared_email_id = null;
      }

      // Validate variables
      if (!validateVariables(subject, body, variablesListArr, toast)) {
        return null;
      }

      // Build final payload
      return {
        ...detailsValues,
        ...emailValues,
        ...fromPayload,
        ats_comm_template_id: selectedTemplate?.id,
        email_template_name:
          selectedTemplate?.name || selectedTemplate?.title || '',
        kind: 'survey',
      };
    },
    [
      feSharedEmails,
      processFromUserPayload,
      selectedTemplate,
      toast,
      isInterviewPlan,
    ]
  );

  const submitSurvey = useCallback(
    async (
      detailsValues: any,
      emailValues: any,
      scheduleValues?: any,
      isSchedule: boolean = false
    ): Promise<SubmitSurveyResult> => {
      // Validate and build payload
      const finalParams = validateAndBuildPayload(detailsValues, emailValues);

      if (!finalParams) {
        return { success: false };
      }

      // Add schedule data if provided
      if (scheduleValues?.schedule_at) {
        finalParams.schedule_at = scheduleValues.schedule_at;
      }

      // Save survey
      const result = await onSave(finalParams);

      if ('data' in result && result?.data?.id) {
        handleSaveSuccess(isSchedule);
        return { success: true, data: result.data };
      } else {
        handleSaveError(result, isSchedule);
        // The resolved sentence rather than the raw envelope: the schedule path
        // toasts this itself, and it was reading `errors` off a result that
        // carries `error` (ENG-4001).
        return {
          success: false,
          errors: result.errors,
          failureMessage: activitySaveFailureMessage(result),
        };
      }
    },
    [validateAndBuildPayload, onSave, handleSaveSuccess, handleSaveError]
  );

  return {
    submitSurvey,
    handleSaveSuccess,
    handleSaveError,
  };
};
